import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ProductCard from "./ProductCard";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Slider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Select,
  MenuItem,
  Rating,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function SearchPage() {
  const query = useQuery().get("q");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [showFilters, setShowFilters] = useState(true);

  // Filter states
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [inStock, setInStock] = useState(false);
  const [bestSellersOnly, setBestSellersOnly] = useState(false);

  // Extract unique filter options from products
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maxPrice, setMaxPrice] = useState(100000);

  // Fetch products on query change
  useEffect(() => {
  if (!query) {
    setProducts([]);
    setLoading(false);
    return;
  }

  const fetchSearchResults = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/search`, {
        params: { q: query },
      });

      const data = res.data;
      const prods = Array.isArray(data) ? data : data.products || [];

      setProducts(prods);

      // Extract unique brands and categories
      const uniqueBrands = [...new Set(prods.map(p => p.brand).filter(Boolean))];
      const uniqueCategories = [...new Set(prods.map(p => p.category).filter(Boolean))];
      const maxPriceValue = Math.max(...prods.map(p => p.price || 0), 100000);

      setBrands(uniqueBrands);
      setCategories(uniqueCategories);
      setMaxPrice(maxPriceValue);
      setPriceRange([0, maxPriceValue]);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchSearchResults();
}, [query]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = products;

    // Price filter
    filtered = filtered.filter(p => {
      const price = p.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Rating filter
    if (selectedRatings.length > 0) {
      filtered = filtered.filter(p => {
        const rating = Math.floor(p.rating || 0);
        return selectedRatings.includes(rating);
      });
    }

    // Brand filter
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(p => selectedCategories.includes(p.category));
    }

    // Stock filter
    if (inStock) {
      filtered = filtered.filter(p => p.stock > 0);
    }

    // Best sellers filter
    if (bestSellersOnly) {
      filtered = filtered.filter(p => p.isBestSeller || p.soldCount > 100);
    }

    // Sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "price_low":
          return (a.price || 0) - (b.price || 0);
        case "price_high":
          return (b.price || 0) - (a.price || 0);
        case "rating":
          return (b.rating || 0) - (a.rating || 0);
        case "newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "bestseller":
          return (b.soldCount || 0) - (a.soldCount || 0);
        case "relevance":
        default:
          return 0;
      }
    });

    setFilteredProducts(sorted);
  }, [products, priceRange, selectedRatings, selectedBrands, selectedCategories, inStock, bestSellersOnly, sortBy]);

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const handleRatingChange = (rating) => {
    setSelectedRatings(prev =>
      prev.includes(rating) ? prev.filter(r => r !== rating) : [...prev, rating]
    );
  };

  const handleBrandChange = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const resetFilters = () => {
    setPriceRange([0, maxPrice]);
    setSelectedRatings([]);
    setSelectedBrands([]);
    setSelectedCategories([]);
    setInStock(false);
    setBestSellersOnly(false);
    setSortBy("relevance");
  };

  const activeFiltersCount = [
    ...selectedRatings,
    ...selectedBrands,
    ...selectedCategories,
    ...(inStock ? [1] : []),
    ...(bestSellersOnly ? [1] : []),
  ].length;

  return (
    <Container maxWidth="xl" sx={{ py: 3, minHeight: "90vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Results for "{query}"
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {filteredProducts.length} results found
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* SIDEBAR - FILTERS */}
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2, position: "sticky", top: 80 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                  <TuneIcon fontSize="small" /> Filters
                </Typography>
                {activeFiltersCount > 0 && (
                  <Chip label={activeFiltersCount} size="small" color="primary" />
                )}
              </Box>

              {activeFiltersCount > 0 && (
                <Button size="small" onClick={resetFilters} sx={{ mb: 2, width: "100%" }}>
                  Clear All Filters
                </Button>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* PRICE RANGE FILTER */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Price Range
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={handlePriceChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={maxPrice}
                  step={1000}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  ₹{priceRange[0].toLocaleString()} - ₹{priceRange[1].toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* RATING FILTER */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Customer Reviews
                </Typography>
                <FormGroup>
                  {[5, 4, 3, 2, 1].map(rating => (
                    <FormControlLabel
                      key={rating}
                      control={
                        <Checkbox
                          checked={selectedRatings.includes(rating)}
                          onChange={() => handleRatingChange(rating)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Rating value={rating} readOnly size="small" />
                          <Typography variant="caption">{rating}★ & up</Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* CATEGORY FILTER */}
              {categories.length > 0 && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Category
                    </Typography>
                    <FormGroup>
                      {categories.slice(0, 5).map(category => (
                        <FormControlLabel
                          key={category}
                          control={
                            <Checkbox
                              checked={selectedCategories.includes(category)}
                              onChange={() => handleCategoryChange(category)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {category}
                            </Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              {/* BRAND FILTER */}
              {brands.length > 0 && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Brand
                    </Typography>
                    <FormGroup>
                      {brands.slice(0, 6).map(brand => (
                        <FormControlLabel
                          key={brand}
                          control={
                            <Checkbox
                              checked={selectedBrands.includes(brand)}
                              onChange={() => handleBrandChange(brand)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {brand}
                            </Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              {/* AVAILABILITY FILTER */}
              <Box sx={{ mb: 3 }}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={inStock} onChange={(e) => setInStock(e.target.checked)} size="small" />}
                    label={<Typography variant="body2">In Stock Only</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={bestSellersOnly} onChange={(e) => setBestSellersOnly(e.target.checked)} size="small" />
                    }
                    label={<Typography variant="body2">Best Sellers</Typography>}
                  />
                </FormGroup>
              </Box>
            </Paper>
          </Grid>

          {/* MAIN CONTENT */}
          <Grid item xs={12} md={10}>
            {/* TOP BAR - SORT OPTIONS */}
            <Paper sx={{ p: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Showing {filteredProducts.length} results
              </Typography>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <MenuItem value="relevance">Relevance</MenuItem>
                  <MenuItem value="price_low">Price: Low to High</MenuItem>
                  <MenuItem value="price_high">Price: High to Low</MenuItem>
                  <MenuItem value="rating">Highest Rated</MenuItem>
                  <MenuItem value="bestseller">Best Sellers</MenuItem>
                  <MenuItem value="newest">Newest Arrivals</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            {/* PRODUCTS GRID */}
            {filteredProducts.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No products found
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Try adjusting your filters or search terms
                </Typography>
              </Paper>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "16px",
                marginTop: "20px"
              }}>
                {filteredProducts.map(product => (
                  <Grid item xs={12} sm={6} lg={4} key={product._id}>
                    <ProductCard product={product} />
                  </Grid>
                ))}
               </div>
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}